/* InputTextTagsControlRenderer.js */

"use strict";
const InputTags = require("./vendor/script");

class InputTextTagsControlRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("input-text-tags-control");
        this._errorSpan = document.createElement("span");
        this._errorSpan.classList.add("error");
        this._tagsRoot = document.createElement("div");
        this._tagsRoot.classList.add("simple-tags");
        this._rootElem.appendChild(this._tagsRoot);
        this._rootElem.appendChild(this._errorSpan);
        this._rootElem.component = this._model;
        this._textField = undefined;
        this._inputTags = undefined;

        // Bind listeners
        this._setFocus = this._setFocus.bind(this);
        this.render();
        this._registerListeners();
    }
    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/
    _shutdown() {
        let prom = new Promise((resolve /*, reject*/) => {
            this._unregisterListeners();
            this._model = undefined;
            this._rootElem.remove();
            this._textField = undefined;
            this._rootElem = undefined;
            this._inputTags = undefined;
            resolve();
        });
        return prom;
    }
    _registerListeners() {
        this._model.on("Disabled", (this._onDisabled = this._onDisabled.bind(this)));
        this._model.on("ReadOnly", (this._onReadOnly = this._onReadOnly.bind(this)));
        this._model.on("Value", (this._onValue = this._onValue.bind(this)));
        this._model.on("Visible", (this._onVisible = this._onVisible.bind(this)));
        this._model.on("Placeholder", (this._onPlaceholder = this._onPlaceholder.bind(this)));
        this._model.on("DarkTheme", (this._onDarkTheme = this._onDarkTheme.bind(this)));
        this._model.on("Theme", (this._onTheme = this._onTheme.bind(this)));
        this._model.on("Focus", (this._onFocus = this._onFocus.bind(this)));
        this._model.on("Blur", (this._onBlur = this._onBlur.bind(this)));
        this._model.on("SetAttribute", (this._onSetAttribute = this._onSetAttribute.bind(this)));
        this._model.on("Title", (this._onTitle = this._onTitle.bind(this)));
        this._model.on("Required", (this._onRequired = this._onRequired.bind(this)));
        this._textField.addEventListener(
            "focus",
            (this._onFocusEvent = this._onFocusEvent.bind(this))
        );
        this._textField.addEventListener(
            "blur",
            (this._onBlurEvent = this._onBlurEvent.bind(this))
        );
        this._textField.addEventListener(
            "keydown",
            (this._onKeyDownEvent = this._onKeyDownEvent.bind(this))
        );
    }
    _unregisterListeners() {
        this._model.removeListener("Disabled", this._onDisabled);
        this._model.removeListener("ReadOnly", this._onReadOnly);
        this._model.removeListener("Value", this._onValue);
        this._model.removeListener("Visible", this._onVisible);
        this._model.removeListener("Placeholder", this._onPlaceholder);
        this._model.removeListener("DarkTheme", this._onDarkTheme);
        this._model.removeListener("Theme", this._onTheme);
        this._model.removeListener("Focus", this._onFocus);
        this._model.removeListener("Blur", this._onBlur);
        this._model.removeListener("SetAttribute", this._onSetAttribute);
        this._model.removeListener("Title", this._onTitle);
        this._model.removeListener("Required", this._onRequired);
        this._textField.removeEventListener("focus", this._onFocusEvent);
        this._textField.removeEventListener("blur", this._onBlurEvent);
        this._textField.removeEventListener("keydown", this._onKeyDownEvent);
    }

    registerTagsListeners() {
        this._inputTags.on("UserValue", (this._onInputEvent = this._onInputEvent.bind(this)));
    }
    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/
    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        let prom = new Promise((resolve /*, reject*/) => {
            this._inputTags = new InputTags(this._tagsRoot);
            this._textField = this._inputTags.input;
            this.registerTagsListeners();
            resolve();
        });
        return prom.then(() => {
            // let isInvalid = this._model.invalid;
            this._setPlaceholder(this._model.placeholder);
            if (this._model.disabled) {
                this._setDisabled(this._model.disabled);
            }
            if (this._model.readOnly && !this._model.disabled) {
                this._setReadOnly(this._model.readOnly); //Preference to disabled
            }
            this._setValue(this._model.value);
            this._setVisible(this._model.visible);
            this._setDarkTheme(this._model.darkTheme);
            this._setTheme(this._model.theme);
            this._setTitle(this._model.title);
            this._setRequired(this._model.required);
            this._setFocus(true);
            return Promise.resolve();
        });
    }
    get rootElement() {
        return this.getRootElement();
    }
    getRootElement() {
        return this._rootElem;
    }
    get model() {
        return this.getModel();
    }
    getModel() {
        return this._model;
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    /**
     * Fired when the disabled state has been updated in the model
     * @param newVal {Boolean}
     * @private
     */
    _onDisabled(newVal) {
        this._setDisabled(newVal);
    }

    _onReadOnly(newVal) {
        this._setReadOnly(newVal);
    }

    /**
     * Fired when the value has been updated in the model
     * @param newVal {String}
     * @param oldVal {String}
     * @private
     */
    _onValue(newVal /*, oldVal*/) {
        this._setValue(newVal);
    }

    /**
     * Fired when the visible state has been updated in the model
     * @param newVal {Boolean}
     * @private
     */
    _onVisible(newVal) {
        this._setVisible(newVal);
    }

    /**
     * Fired when the placeholder has been updated in the model
     * @param newVal {String}
     * @private
     */
    _onPlaceholder(newVal) {
        this._setPlaceholder(newVal);
    }

    /**
     * Fired when the darkTheme has been updated in the model
     * @param newVal {Boolean}
     * @private
     */
    _onDarkTheme(newVal) {
        this._setDarkTheme(newVal);
    }

    /**
     * Fired when the theme has been updated in the model
     * @param newVal {String}
     * @param oldVal {String}
     * @private
     */
    _onTheme(newVal, oldVal) {
        this._setTheme(newVal, oldVal);
    }

    /**
     * Fired when focus has been called in the model
     * @private
     */
    _onFocus() {
        this._setFocus(true);
    }

    /**
     * Fired when blur has been called in the model
     * @private
     */
    _onBlur() {
        this._setFocus(false);
    }

    /**
     * Fired when setAttribute has been called in the model
     * @param attrName {String} - The name of the HTML attribute to be set on the native control
     * @param attrValue {String|Undefined} - The value of the HTML attribute to be set on the native control
     * @private
     */
    _onSetAttribute(attrName, attrValue) {
        this._setAttribute(attrName, attrValue);
    }

    /**
     * Fired when setTitle has been called on the model
     * @param newVal {String} - title to be set on the control
     * @private
     */
    _onTitle(newVal) {
        this._setTitle(newVal);
    }

    _onRequired(newRequiredVal) {
        this._setRequired(newRequiredVal);
    }

    /**
     * Update the value in the model based on user input
     * @param event {Event}
     * @private
     */
    _onInputEvent(event) {
        let newVal = event;
        this._model.setValue(newVal, true);
        this._model._triggerUserValue(newVal, "", event);
    }

    /**
     * Pass the focus event up to the model
     * @param event {Event}
     * @private
     */
    _onFocusEvent(event) {
        this._model._triggerUserFocus(event);
    }

    /**
     * Pass the blur event up to the model
     * @param event {Event}
     * @private
     */
    _onBlurEvent(event) {
        this._model._triggerUserBlur(event);
    }

    /**
     * Pass the blur event up to the model
     * @param event {Event}
     * @private
     */
    _onKeyDownEvent(event) {
        this._model._triggerUserKeyDown(event);
    }

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE API METHODS */
    /*******************************************/

    /**
     * Toggle the disabled state
     * @param newVal {Boolean}
     * @private
     */
    _setDisabled(newVal) {
        this._inputTags.setDisabled(newVal);
    }

    _setReadOnly(newVal) {
        this._inputTags.setReadOnly(newVal);
    }

    /**
     * Put the placeholder text in place and move the cursor to the front of the selection if placeholder text
     * is displaying.
     * @param newVal {String}
     * @private
     */
    _setPlaceholder(newVal) {
        // if (newVal !== undefined && newVal !== null && newVal !== "") {
        //     this._textField.placeholder = newVal;
        // } else {
        //     this._textField.placeholder = '';
        // }
        this._inputTags.setPlaceholder(newVal);
    }

    /**
     * Toggle the display text
     * @param newVal {String}
     * @private
     */
    _setValue(newVal) {
        let inputVal = newVal.toString();
        if (inputVal !== (this._inputTags.value && this._inputTags.value.toString())) {
            if (newVal) {
                this._inputTags.value = newVal;
            } else {
                this._inputTags.value = null;
                if (this._model.value !== null) {
                    this._model.value = null;
                }
            }
        }
    }

    /**
     * Toggle the visible state
     * @param newVal {Boolean}
     * @private
     */
    _setVisible(newVal) {
        if (newVal === true) {
            this._rootElem.classList.remove("hidden");
        } else {
            this._rootElem.classList.add("hidden");
        }
    }

    /**
     * Toggle or set the dark theme
     * @param newVal {Boolean}
     * @private
     */
    _setDarkTheme(newVal) {
        if (newVal === true) {
            this._rootElem.classList.add("dark");
        } else {
            this._rootElem.classList.remove("dark");
        }
    }

    /**
     * Set the theme name
     * @param newVal {String}
     * @param oldVal {String}
     * @private
     */
    _setTheme(newVal, oldVal) {
        if (oldVal) {
            this._rootElem.classList.remove(oldVal);
        }
        if (newVal) {
            this._rootElem.classList.add(newVal);
        }
    }

    /**
     * Set the title/tooltip
     * @param newVal {String}
     * @private
     */
    _setTitle(newVal) {
        if (newVal) {
            this._rootElem.setAttribute("title", newVal);
        } else {
            this._rootElem.setAttribute("title", "");
        }
    }

    // eslint-disable-next-line no-unused-vars
    _setRequired(newRequiredVal) {
        // if(newRequiredVal === true) {
        //     this._textField.setAttribute('required', '');
        // } else {
        //     this._textField.removeAttribute('required');
        // }
        // this._setInvalid(!this._textField.validity.valid);
    }

    /**
     * Set or remove focus on the textfield
     * @param newVal {Boolean}
     * @private
     */
    _setFocus(newVal) {
        this._inputTags.setFocus(newVal);
    }

    /**
     * Set an attribute on the native control
     * @param attrName {String} - The name of the HTML attribute to be set on the native control
     * @param attrValue {String|Undefined} - The value of the HTML attribute to be set on the native control
     * @private
     */
    // eslint-disable-next-line no-unused-vars
    _setAttribute(attrName, attrValue) {
        // if (!_.isUndefined(attrValue)) {
        //     this._textField.setAttribute(attrName, attrValue);
        // } else {
        //     this._textField.removeAttribute(attrName);
        // }
    }

    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/
}

module.exports = InputTextTagsControlRenderer;
